import React, { useRef, useState, useCallback } from 'react';
import { Camera, RotateCcw, Check, X } from 'lucide-react';

const CameraCapture = ({ onCapture, onCancel, instructions }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'environment' // Prefer back camera on mobile
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsStreaming(true);
      }
    } catch (err) {
      setError('Camera access denied or not available');
      console.error('Camera error:', err);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsStreaming(false);
    }
  }, []);

  const captureImage = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        setCapturedImage(url);
        stopCamera();
      }, 'image/jpeg', 0.8);
    }
  }, [stopCamera]);

  const confirmCapture = useCallback(() => {
    if (capturedImage && onCapture) {
      onCapture(capturedImage);
    }
  }, [capturedImage, onCapture]);

  const retakePhoto = useCallback(() => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    startCamera();
  }, [capturedImage, startCamera]);

  const handleCancel = useCallback(() => {
    stopCamera();
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    if (onCancel) {
      onCancel();
    }
  }, [stopCamera, capturedImage, onCancel]);

  React.useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  if (error) {
    return (
      <div className="camera-error">
        <div className="error-content">
          <Camera className="w-12 h-12 text-red-500 mb-4" />
          <h3>Camera Unavailable</h3>
          <p>{error}</p>
          <p className="text-sm text-gray-600 mt-2">
            Please ensure camera permissions are enabled and try again.
          </p>
          <button onClick={handleCancel} className="btn btn-secondary mt-4">
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="camera-capture-container">
      {instructions && (
        <div className="camera-instructions">
          <p>{instructions}</p>
        </div>
      )}

      <div className="camera-viewport">
        {capturedImage ? (
          <img 
            src={capturedImage} 
            alt="Captured" 
            className="captured-image"
          />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="camera-video"
            />
            {!isStreaming && (
              <div className="camera-loading">
                <div className="loading-spinner"></div>
                <p>Starting camera...</p>
              </div>
            )}
          </>
        )}
        
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>

      <div className="camera-controls">
        {capturedImage ? (
          <>
            <button 
              onClick={retakePhoto}
              className="btn btn-secondary"
            >
              <RotateCcw className="w-5 h-5" />
              Retake
            </button>
            <button 
              onClick={confirmCapture}
              className="btn btn-primary"
            >
              <Check className="w-5 h-5" />
              Use Photo
            </button>
          </>
        ) : (
          <>
            <button 
              onClick={handleCancel}
              className="btn btn-secondary"
            >
              <X className="w-5 h-5" />
              Cancel
            </button>
            <button 
              onClick={captureImage}
              className="capture-button"
              disabled={!isStreaming}
            >
              <Camera className="w-8 h-8" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CameraCapture;
